/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable no-continue */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useField } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import { Checkbox } from '@folio/stripes/components';
import { chain, get } from 'lodash';
import useLocationName from '../../../../../hooks/useLocationName';
import ItemAccordionHeader from './ItemAccordionHeader';
import { useFormPageErrorsContext } from '../../../../../providers/FormPagesErrorsProvider';
import css from '../pageSharedStyles.css';

export function getMissingRequiredFieldNamesFromTemplate(template, itemData) {
  const requiredFields = chain(template)
    .map('sectionFields')
    .flatten()
    .filter({ isRequired: true })
    .value();

  return requiredFields
    .filter((field) => {
      const value = get(itemData, field.name);
      return value === undefined || value === null || value === '';
    })
    .map((field) => field.name);
}

const RenderItem = React.memo(
  ({
    fields,
    index,
    name,
    template,
    holdingsRecordId,
    controlledVocab,
    isDisabledItemActions,
    updateSelection,
    isAccordionChecked,
    onEditItem,
    createUndoAction,
    isHighlighted = false,
  }) => {
    const [localHighlight, setLocalHighlight] = useState(false);

    // Listen for highlighting changes
    useEffect(() => {
      if (isHighlighted && !localHighlight) {
        setLocalHighlight(true);
        setTimeout(() => {
          setLocalHighlight(false);
        }, 3000);
      }
    }, [isHighlighted, localHighlight, index]);

    // Use local highlight state for styling
    const shouldHighlight = localHighlight || isHighlighted;
    const itemLocationField = useField(
      `itemsData.${holdingsRecordId}[${index}].permanentLocation.id`,
    );
    const permanentLocationId = itemLocationField.input.value;
    const { locationName, locationCode } = useLocationName(
      permanentLocationId,
      controlledVocab,
    );

    const barcodeField = useField(
      `itemsData.${holdingsRecordId}[${index}].barcode`,
    );

    const [hasError, setHasError] = useState(false);
    const { updateError } = useFormPageErrorsContext();

    useEffect(() => {
      const item = fields.value[index];
      const itemId = item?.id;

      if (!itemId) return;

      const missing = getMissingRequiredFieldNamesFromTemplate(
        template?.templateFields?.items,
        item,
      );

      const hasMissing = Boolean(missing.length);
      setHasError(hasMissing);
      updateError(itemId, hasMissing);
    }, [fields.value[index]]);

    return (
      <div
        className={
          hasError
            ? `${css.accordionErrorAnimated} ${css.accordionErrorAnimatedActive}`
            : ''
        }
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '10px',
          padding: hasError ? '8px 10px' : '5px 10px',
          marginBottom: hasError ? '5px' : '2px',
          backgroundColor: shouldHighlight
            ? '#e3f2fd'
            : hasError
              ? '#fff4f4'
              : '#fafafa',
          border: shouldHighlight
            ? '1px solid #2196f3'
            : hasError
              ? '1px solid #f5c6cb'
              : '1px solid #e9ecef',
          borderRadius: '4px',
          minHeight: '48px',
          transition: 'all 0.3s ease-in-out',
          boxShadow: shouldHighlight
            ? '0 2px 8px rgba(33, 150, 243, 0.15)'
            : 'none',
        }}
      >
        <Checkbox
          value={index}
          onChange={updateSelection}
          checked={isAccordionChecked}
          onMouseDown={(e) => e.preventDefault()}
        />

        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            fontSize: '17px',
            fontWeight: 'bold',
            color: hasError ? '#721c24' : '#333',
          }}
        >
          <FormattedMessage
            id="ui-plugin-inventory-templates.itemsList.item.title"
            values={{
              copyNumber: fields.value[index]?.copyNumber,
              location: permanentLocationId ? locationName : '',
              barcode: barcodeField.input.value
                ? ` - ${barcodeField.input.value}`
                : '',
            }}
            defaultMessage="Copy {copyNumber} [{location}{barcode}]"
          />
        </div>

        {hasError && (
          <div
            style={{
              fontSize: '12px',
              color: '#721c24',
              fontStyle: 'italic',
            }}
          >
            <FormattedMessage
              id="ui-plugin-inventory-templates.validation.requiredFieldsMissing"
              defaultMessage="Required fields missing"
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <ItemAccordionHeader
            index={index}
            fields={fields}
            permanentLocationId={permanentLocationId}
            locationCode={locationCode}
            controlledVocab={controlledVocab}
            isDisabledItemActions={isDisabledItemActions}
            template={template}
            onEditItem={onEditItem}
            createUndoAction={createUndoAction}
          />
        </div>
      </div>
    );
  },
);

RenderItem.propTypes = {
  index: PropTypes.number,
  name: PropTypes.string,
  template: PropTypes.object,
  fields: PropTypes.object,
  holdingsRecordId: PropTypes.string,
  controlledVocab: PropTypes.object,
  isDisabledItemActions: PropTypes.bool,
  updateSelection: PropTypes.func,
  isAccordionChecked: PropTypes.bool,
  onEditItem: PropTypes.func,
  createUndoAction: PropTypes.func,
  isHighlighted: PropTypes.bool,
};

export default RenderItem;
